import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FaUsers, FaHandshake, FaVoteYea, FaShieldAlt,
  FaCalendarAlt, FaChartLine, FaCheckCircle,
  FaExternalLinkAlt, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import './CommunityPods.css';

const CommunityPods = () => {
  const [activePod, setActivePod]       = useState(0);
  const [time, setTime]                 = useState(0);
  const [isPaused, setIsPaused]         = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoScroll, setAutoScroll]     = useState(true);

  const featuresScrollRef  = useRef(null);
  const useCasesTrackRef   = useRef(null);
  const bankLogosTrackRef  = useRef(null);

  const pods = [
    {
      id: 1, name: "College Batch Pod", members: 200,
      fund: "₹5,00,000", interest: "12% p.a.", color: "#6C63FF",
      description: "Batch of 2020 alumni collective fund for career development",
      rules: ["Max loan: ₹50,000 per member","2 loans per year maximum","3 signatories required","6-12 month repayment","Emergency loan: 48h approval","Monthly meeting compulsory"]
    },
    {
      id: 2, name: "Society Residents Pod", members: 150,
      fund: "₹8,50,000", interest: "10% p.a.", color: "#FF6584",
      description: "Apartment society emergency and maintenance fund",
      rules: ["Max loan: ₹1,00,000 per family","Emergency loans: 24h approval","5 signatories required","Flexible repayment","Maintenance fee included","Insurance coverage"]
    },
    {
      id: 3, name: "Farmers Group Pod", members: 100,
      fund: "₹12,00,000", interest: "8% p.a.", color: "#00D4AA",
      description: "Seasonal crop funding and equipment purchase",
      rules: ["Max loan: ₹2,00,000 per farmer","Crop cycle based repayment","Government subsidy integration","3 signatories required","Weather insurance","Equipment sharing"]
    },
    {
      id: 4, name: "Office Team Pod", members: 50,
      fund: "₹3,20,000", interest: "15% p.a.", color: "#9D4EDD",
      description: "Employee welfare and emergency fund",
      rules: ["Max loan: 3 months salary","HR approval required","EMI deduction from salary","2 signatories required","Medical emergency priority","Training fund available"]
    }
  ];

  const bankLogos = [
    { name: "HDFC Bank",    logo: "/Assets/hdfc_bank.png" },
    { name: "Axis Bank",    logo: "/Assets/axis_bank.png" },
    { name: "ICICI Bank",   logo: "/Assets/icici_bank.png" },
    { name: "Canara Bank",  logo: "/Assets/canara_bank.png" },
    { name: "Bandhan Bank", logo: "/Assets/bandhan_bank.png" },
    { name: "SBI Bank",     logo: "/Assets/sbi_bank.png" },
    { name: "Kotak Bank",   logo: "/Assets/kotak_bank.png" },
    { name: "Yes Bank",     logo: "/Assets/yes_bank.png" }
  ];

  const features = [
    { icon: <FaVoteYea />,         title: "Democratic Governance",   description: "Interest rates and rules decided by majority voting" },
    { icon: <FaHandshake />,       title: "Multi-Signature Security", description: "Withdrawals need approval from multiple authorized members" },
    { icon: <FaUsers />,           title: "Flexible Rules",           description: "Customize loan limits, tenure, and eligibility criteria" },
    { icon: <FaShieldAlt />,       title: "Secure Escrow",           description: "Funds held securely with partner banks" },
    { icon: <FaCalendarAlt />,     title: "Smart Automation",        description: "Automated collections and smart contract execution" },
    { icon: <FaChartLine />,       title: "Live Dashboard",          description: "Real-time visualization of fund growth and analytics" },
    { icon: <FaCheckCircle />,     title: "Transparent Audit",       description: "Complete transaction history and audit trail" },
    { icon: <FaExternalLinkAlt />, title: "Easy Integration",        description: "Seamless integration with banking and payment systems" }
  ];

  const useCases = ["College Batchmates","Apartment Societies","Office Teams","Self Help Groups","Farmers Cooperatives","Village Committees"];

  // Animation loop
  useEffect(() => {
    if (isPaused) return;
    let id;
    let last = performance.now();
    const animate = (now) => {
      setTime(t => t + (now - last) / 1000);
      last = now;
      id = requestAnimationFrame(animate);
    };
    id = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(id);
  }, [isPaused]);

  const calculatePodPosition = (index) => {
    const angle  = time * 0.3 + (index * Math.PI / 2);
    const w      = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const radius = w <= 360 ? 85 : w <= 480 ? 100 : w <= 768 ? 120 : 175;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return { transform: `translate(calc(${x}px - 50%), calc(${y}px - 50%))`, left: '50%', top: '50%' };
  };

  const scrollToHeader = () => {
    document.querySelector('header')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll features
  useEffect(() => {
    if (!autoScroll || !featuresScrollRef.current) return;
    const id = setInterval(() => {
      const c = featuresScrollRef.current;
      if (!c) return;
      if (c.scrollLeft + c.clientWidth >= c.scrollWidth - 10) {
        c.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        c.scrollTo({ left: c.scrollLeft + 300, behavior: 'smooth' });
      }
    }, 3000);
    return () => clearInterval(id);
  }, [autoScroll]);

  // Use cases animation
  useEffect(() => {
    const track = useCasesTrackRef.current;
    if (!track) return;
    track.style.animation = 'scrollUseCases 40s linear infinite';
    return () => { track.style.animation = 'none'; };
  }, []);

  // Bank logos animation
  useEffect(() => {
    const track = bankLogosTrackRef.current;
    if (!track) return;
    track.style.animation = 'scrollBankLogos 35s linear infinite';
    return () => { track.style.animation = 'none'; };
  }, []);

  const scrollFeatures = (dir) => {
    if (!featuresScrollRef.current) return;
    const c = featuresScrollRef.current;
    c.scrollTo({ left: c.scrollLeft + (dir === 'left' ? -300 : 300), behavior: 'smooth' });
    setAutoScroll(false);
    setTimeout(() => setAutoScroll(true), 5000);
  };

  return (
    <section className="community-pods-section" id="community-pods">
      <div className="container">

        {/* Section header */}
        <div className="section-header">
          <h2 className="instant-load">Community Lending Pods</h2>
          <p className="instant-load">Create your own mini-bank with trusted communities</p>
        </div>

        {/* Use cases scrolling */}
        <div className="use-cases-scrolling-section">
          <div className="use-cases-scroll-wrapper">
            <div className="use-cases-track" ref={useCasesTrackRef}>
              {[...useCases, ...useCases].map((useCase, i) => (
                <span
                  key={i}
                  className="use-case-item"
                  style={{
                    backgroundColor: pods[i % pods.length].color + '12',
                    color:            pods[i % pods.length].color,
                    borderColor:      pods[i % pods.length].color + '35'
                  }}
                >
                  {useCase}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Pods container */}
        <div className="pods-container">

          {/* Orbit system */}
          <div
            className="pods-visual orbit-system"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="orbit-container">
              <div className="orbit-path outer-orbit" />
              <div className="orbit-path inner-orbit" />

              {pods.map((pod, index) => (
                <div key={pod.id} className="pod-orbit-item" style={calculatePodPosition(index)}>
                  <motion.div
                    className={`pod-node ${activePod === index ? 'active' : ''}`}
                    style={{ '--pod-color': pod.color }}
                    onClick={() => setActivePod(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                  >
                    <div className="pod-arc-top">
                      <svg viewBox="0 0 200 60" className="arc-text-svg">
                        <defs>
                          <path id={`arcTop-${pod.id}`} d="M 25,40 Q 100,0 175,40" fill="transparent" />
                        </defs>
                        <text className="arc-text-element">
                          <textPath href={`#arcTop-${pod.id}`} startOffset="50%" textAnchor="middle" fill={pod.color} dominantBaseline="middle">
                            {pod.name}
                          </textPath>
                        </text>
                      </svg>
                    </div>
                    <div className="pod-center-icon">
                      <div className="icon-circle"><FaUsers className="pod-icon" /></div>
                      <div className="icon-glow" style={{ backgroundColor: pod.color }} />
                    </div>
                    <div className="pod-bottom-members">
                      <div className="members-wrapper">
                        <div className="members-label">MEMBERS</div>
                        <div className="members-count" style={{ color: pod.color }}>{pod.members}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}

              <motion.div
                className="central-fund"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="fund-circle-content">
                  <h3>Community Funds</h3>
                  <div className="fund-amount">₹28,70,000</div>
                  <p>1,200+ Active Pods</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Pod details */}
          <div className="pod-details">
            <motion.div
              key={activePod}
              className="pod-detail-card"
              style={{ '--pod-color': pods[activePod].color }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.28 }}
            >
              <div className="pod-header" style={{ backgroundColor: pods[activePod].color + '10' }}>
                <h3>{pods[activePod].name}</h3>
                <div className="pod-header-divider" style={{ backgroundColor: pods[activePod].color }} />
                <div className="pod-metrics-three-box">
                  {[
                    { value: pods[activePod].members, label: 'Members' },
                    { value: pods[activePod].fund,    label: 'Total Fund' },
                    { value: pods[activePod].interest, label: 'Interest Rate' }
                  ].map(m => (
                    <div key={m.label} className="metric-box" style={{ borderColor: pods[activePod].color + '40' }}>
                      <div className="metric-value">{m.value}</div>
                      <div className="metric-label">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pod-body">
                <p className="pod-description">{pods[activePod].description}</p>
                <div className="pod-rules">
                  <h4><span className="rules-icon">⚖️</span> Pod Rules</h4>
                  <ul>
                    {pods[activePod].rules.map((rule, i) => (
                      <li key={i}>
                        <FaCheckCircle style={{ color: pods[activePod].color }} />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pod-actions-single">
                  <button
                    className="create-app-btn"
                    onClick={scrollToHeader}
                    style={{
                      backgroundColor: pods[activePod].color,
                      boxShadow: `0 4px 20px ${pods[activePod].color}40`
                    }}
                  >
                    <FaExternalLinkAlt className="btn-icon" />
                    Create Pod on our app
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bank partners */}
        <div className="bank-partners-section">
          <div className="section-header">
            <h3 className="instant-load">Our Partner Banks</h3>
          </div>
          <div className="bank-logos-scroll-wrapper">
            <div className="bank-logos-track" ref={bankLogosTrackRef}>
              {[...bankLogos, ...bankLogos, ...bankLogos].map((bank, i) => (
                <div key={i} className="bank-logo-item">
                  <img src={bank.logo} alt={bank.name} className="bank-logo-image" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key features */}
        <div className="pod-features-section">
          <div className="section-header">
            <h2 className="instant-load">Key Features</h2>
            <p className="instant-load">Everything you need to manage your community fund efficiently</p>
          </div>

          <div className="features-container-wrapper">
            <button className="scroll-button left" onClick={() => scrollFeatures('left')} aria-label="Scroll left">
              <FaChevronLeft />
            </button>

            <div
              className="features-scroll-container"
              ref={featuresScrollRef}
              onMouseEnter={() => setAutoScroll(false)}
              onMouseLeave={() => setAutoScroll(true)}
            >
              <div className="features-scroll-wrapper">
                {[...features, ...features].map((feature, i) => (
                  <motion.div
                    key={i}
                    className="feature-box"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: (i % features.length) * 0.06 }}
                  >
                    <div className="feature-box-icon" style={{ color: pods[i % pods.length].color }}>
                      {feature.icon}
                    </div>
                    <div className="feature-box-content">
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <button className="scroll-button right" onClick={() => scrollFeatures('right')} aria-label="Scroll right">
              <FaChevronRight />
            </button>
          </div>

          <div className="scroll-indicator-dots">
            {features.map((_, i) => (
              <span
                key={i}
                className={`dot ${currentSlide === i ? 'active' : ''}`}
                onClick={() => {
                  featuresScrollRef.current?.scrollTo({ left: i * 300, behavior: 'smooth' });
                  setCurrentSlide(i);
                }}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default CommunityPods;